import { useAuthentication } from "services";
import axios, { CancelToken } from "axios";
import notify from "devextreme/ui/notify";
import { NonCompletedLoadsByDriverResponse } from "types/nonCompletedLoadsByDriverResponse";
import { FuelPlanResponse, StopDetailsResponse } from "types";
import {
  DispatchFuelPlanResponse,
  normalizeFuelPlanResponse,
} from "./fuel-plan-response-adapter";

const useDispatchService = () => {
  const { getAuthHeader } = useAuthentication();
  const serviceUrl = `${process.env.REACT_APP_DISPATCH_SERVICE}`;

  const getDriverConfirmations = async (
    driverId: string
  ): Promise<NonCompletedLoadsByDriverResponse> => {
    let response = await axios.get(
      `${serviceUrl}/Load/NonCompletedLoadsByDriver?driverId=${driverId}`,
      {
        headers: await getAuthHeader(),
      }
    );

    return response.data;
  };

  const getStopDetails = async ({
    loadNumber,
    stopId,
    useOdsVersion,
    odsStopEventId,
  }: {
    loadNumber?: string;
    stopId?: number;
    useOdsVersion?: boolean;
    odsStopEventId?: number;
  }): Promise<StopDetailsResponse> => {
    try {
      const params = new URLSearchParams();

      if (useOdsVersion && odsStopEventId && odsStopEventId > 0) {
        params.append("odsStopEventId", `${odsStopEventId}`);
      } else {
        if (stopId && stopId > 0) {
          params.append("stopId", `${stopId}`);
        }

        if (loadNumber) {
          params.append("loadNumber", loadNumber);
        }
      }

      let response = await axios.get(
        `${serviceUrl}/Load/StopDetails?${params.toString()}`,
        {
          headers: await getAuthHeader(),
        }
      );

      if (response && response.data) {
        return response.data;
      }
      return {} as StopDetailsResponse;
    } catch (error) {
      console.log(error);
      notify("Error retrieving stop information.", "error", 5000);
      return Promise.resolve({} as StopDetailsResponse);
    }
  };

  const confirmLoad = async (loadNumber: string): Promise<any> => {
    let response = await axios.post(
      `${serviceUrl}/Load/ConfirmLoad?loadNumber=${loadNumber}`,
      null,
      {
        headers: await getAuthHeader(),
      }
    );
    return response.data;
  };

  const getDriverFeatureEligibility = async (
    driverId: string,
    featureKey: string
  ): Promise<boolean> => {
    const params = new URLSearchParams();
    params.append("driverId", driverId);
    params.append("featureKey", featureKey);

    const response = await axios.get(
      `${serviceUrl}/DriverFeatureEnrollment/Eligibility?${params.toString()}`,
      {
        headers: await getAuthHeader(),
      }
    );

    return response.data?.eligible === true;
  };

  const getFuelPlanByLoadNumber = async (
    loadNumber: string,
    cancelToken?: CancelToken,
    captureResponse?: (
      response: FuelPlanResponse | DispatchFuelPlanResponse
    ) => void
  ): Promise<FuelPlanResponse> => {
    let response = await axios.get(
      `${serviceUrl}/FuelPlan/ByLoadNumber?loadNumber=${encodeURIComponent(
        loadNumber
      )}`,
      {
        headers: await getAuthHeader(),
        cancelToken,
      }
    );

    captureResponse?.(response.data);
    return normalizeFuelPlanResponse(response.data);
  };

  return {
    getDriverConfirmations: getDriverConfirmations,
    getStopDetails: getStopDetails,
    confirmLoad: confirmLoad,
    getDriverFeatureEligibility: getDriverFeatureEligibility,
    getFuelPlanByLoadNumber: getFuelPlanByLoadNumber,
  };
};

export default useDispatchService;
